import { onAuthStateChanged } from "firebase/auth";
import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase-config";
import { useNavigate } from "react-router-dom";
import { collection, deleteDoc, deleteField, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from '../redux/userReducer'
import { getFirebaseDocs, randomString } from '../common'
import Header from '../components/Header'
import Input from "../components/Input";
import MyDropzone from "../components/MyDropzone";
import Tabs from "../components/Tabs";
import ModalComp from "../components/Modal";

const SHOP_SETTINGS_COLLECTION = 'shop-config'
const SHOP_SETTINGS_DOC = 'global'
const SHOP_PRODUCTS_COLLECTION = 'shop-products'
const SHOP_ORDERS_COLLECTION = 'shop-orders'
const DEFAULT_CATEGORIES = ['Uniform', 'Sports', 'Music', 'Gymnastics', 'Yoga']
const ORDER_STATUSES = ['Placed', 'Paid', 'Processing', 'Shipped', 'Delivered', 'Cancelled']

const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

const normalizeProductImages = (product) => {
    if (!product) return []
    if (Array.isArray(product.images)) {
        const urls = product.images.map((url) => (url || '').trim()).filter(Boolean)
        if (urls.length > 0) return urls
    }
    const fallback = (product.image || '').trim()
    return fallback ? [fallback] : []
}

const getFunctionUrl = (name) => `https://${name.toLowerCase()}-ponxxqtrpa-km.a.run.app/`

const Shop = () => {

    const [activeTab, setActiveTab] = useState('Products')
    const [loading, setLoading] = useState(false)

    const [settings, setSettings] = useState({
        categories: DEFAULT_CATEGORIES,
        deliveryFee: 30,
        bannerImage: '',
    })
    const [settingsCategoriesCsv, setSettingsCategoriesCsv] = useState(DEFAULT_CATEGORIES.join(','))

    const [products, setProducts] = useState([])
    const [orders, setOrders] = useState([])
    const [showProductForm, setShowProductForm] = useState(false)
    const [deleteProductId, setDeleteProductId] = useState('')

    const [editId, setEditId] = useState('')
    const [title, setTitle] = useState('')
    const [category, setCategory] = useState(DEFAULT_CATEGORIES[0])
    const [price, setPrice] = useState('')
    const [quantity, setQuantity] = useState('')
    const [description, setDescription] = useState('')
    const [productImages, setProductImages] = useState([{ id: randomString(8), url: '' }])
    const [active, setActive] = useState(true)
    const [hasVariants, setHasVariants] = useState(false)
    const [variants, setVariants] = useState([])

    const [selectedOrderId, setSelectedOrderId] = useState('')
    const [orderStatus, setOrderStatus] = useState('Placed')
    const [trackingNumber, setTrackingNumber] = useState('')
    const [trackingUrl, setTrackingUrl] = useState('')
    const [statusNote, setStatusNote] = useState('')

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const userInfo = useSelector(state => state.user.user)

    const categories = useMemo(() => {
        const fromSettings = (settings.categories || []).filter(Boolean)
        return fromSettings.length > 0 ? fromSettings : DEFAULT_CATEGORIES
    }, [settings])

    const selectedOrder = useMemo(() => orders.find(row => row.id === selectedOrderId), [orders, selectedOrderId])

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                navigate('/login')
                return
            }

            const userDoc = await getDoc(doc(db, 'users', user.uid))
            const userData = userDoc.data()
            if (!userData) {
                navigate('/login')
                return
            }
            dispatch(setUser({ ...userData, id: user.uid }))
        })
        return () => unsubscribe()
    }, [])

    useEffect(() => {
        if (!userInfo) return
        if (userInfo.role === 'staff') {
            navigate('/calendar')
            return
        }
        hydratePage()
    }, [userInfo])

    useEffect(() => {
        const onWheel = (event) => {
            const target = event.target
            if (target && target.tagName === 'INPUT' && target.type === 'number' && document.activeElement === target) {
                target.blur()
            }
        }
        window.addEventListener('wheel', onWheel, { capture: true })
        return () => window.removeEventListener('wheel', onWheel, { capture: true })
    }, [])

    const hydratePage = async () => {
        setLoading(true)
        try {
            await Promise.all([fetchSettings(), fetchProducts(), fetchOrders()])
        } catch (e) {
            console.error(e)
            toast.error('Unable to load shop data.')
        }
        setLoading(false)
    }

    const fetchSettings = async () => {
        const docSnap = await getDoc(doc(db, SHOP_SETTINGS_COLLECTION, SHOP_SETTINGS_DOC))
        if (!docSnap.exists()) {
            const payload = {
                categories: DEFAULT_CATEGORIES,
                deliveryFee: 30,
                bannerImage: '',
                updatedAt: new Date().toISOString(),
            }
            await setDoc(doc(db, SHOP_SETTINGS_COLLECTION, SHOP_SETTINGS_DOC), payload)
            setSettings(payload)
            setSettingsCategoriesCsv(payload.categories.join(','))
            return
        }

        const data = docSnap.data() || {}
        const payload = {
            categories: (data.categories && data.categories.length > 0) ? data.categories : DEFAULT_CATEGORIES,
            deliveryFee: toNumber(data.deliveryFee || 30),
            bannerImage: data.bannerImage || '',
        }
        setSettings(payload)
        setSettingsCategoriesCsv(payload.categories.join(','))
    }

    const fetchProducts = async () => {
        const docs = getFirebaseDocs(await getDocs(collection(db, SHOP_PRODUCTS_COLLECTION)))
        setProducts(docs.sort((a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime()))
    }

    const fetchOrders = async () => {
        const docs = getFirebaseDocs(await getDocs(collection(db, SHOP_ORDERS_COLLECTION)))
        const sorted = docs.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
        setOrders(sorted)
        if (!selectedOrderId && sorted.length > 0) {
            setupOrderEdit(sorted[0])
        }
    }

    const clearProductForm = () => {
        setEditId('')
        setTitle('')
        setCategory(categories[0] || DEFAULT_CATEGORIES[0])
        setPrice('')
        setQuantity('')
        setDescription('')
        setProductImages([{ id: randomString(8), url: '' }])
        setActive(true)
        setHasVariants(false)
        setVariants([])
    }

    const startAddProduct = () => {
        clearProductForm()
        setShowProductForm(true)
    }

    const setupEditProduct = (row) => {
        setEditId(row.id)
        setTitle(row.title || '')
        setCategory(row.category || categories[0] || DEFAULT_CATEGORIES[0])
        setPrice(row.price?.toString() || '')
        setQuantity(row.quantity?.toString() || '')
        setDescription(row.description || '')
        const normalizedImages = normalizeProductImages(row)
        setProductImages((normalizedImages.length > 0 ? normalizedImages : ['']).map((url) => ({ id: randomString(8), url })))
        setActive(row.active !== false)
        setHasVariants(!!row.hasVariants)
        setVariants((row.variants || []).map(variant => ({
            id: variant.id || randomString(8),
            name: variant.name || '',
            value: variant.value || '',
            price: variant.price?.toString() || '',
            quantity: variant.quantity?.toString() || '0',
        })))
        setShowProductForm(true)
    }

    const addVariant = () => {
        setVariants([...variants, { id: randomString(8), name: 'Size', value: '', price: '', quantity: '0' }])
    }

    const updateVariant = (id, field, value) => {
        setVariants(variants.map(row => row.id === id ? { ...row, [field]: value } : row))
    }

    const removeVariant = (id) => {
        setVariants(variants.filter(row => row.id !== id))
    }

    const addProductImageSlot = () => {
        setProductImages((prev) => [...prev, { id: randomString(8), url: '' }])
    }

    const updateProductImage = (id, url) => {
        setProductImages((prev) => prev.map((row) => row.id === id ? { ...row, url } : row))
    }

    const removeProductImage = (id) => {
        setProductImages((prev) => {
            const next = prev.filter((row) => row.id !== id)
            return next.length > 0 ? next : [{ id: randomString(8), url: '' }]
        })
    }

    const saveProduct = async () => {
        if (!title.trim()) {
            toast.error('Title is required.')
            return
        }
        const imageUrls = productImages.map((row) => (row.url || '').trim()).filter(Boolean)
        if (imageUrls.length === 0) {
            toast.error('At least one product image is required.')
            return
        }

        const parsedPrice = toNumber(price)
        if (parsedPrice <= 0) {
            toast.error('Price should be greater than 0.')
            return
        }

        const normalizedVariants = variants
            .map(row => ({
                id: row.id || randomString(8),
                name: (row.name || '').trim(),
                value: (row.value || '').trim(),
                price: toNumber(row.price) > 0 ? toNumber(row.price) : null,
                quantity: Math.max(0, parseInt(row.quantity || 0))
            }))
            .filter(row => row.value)

        if (hasVariants && normalizedVariants.length === 0) {
            toast.error('Please add at least one variant with value.')
            return
        }

        const totalQuantity = hasVariants
            ? normalizedVariants.reduce((sum, row) => sum + row.quantity, 0)
            : Math.max(0, parseInt(quantity || 0))

        const payload = {
            title: title.trim(),
            category: category || DEFAULT_CATEGORIES[0],
            price: parsedPrice,
            quantity: totalQuantity,
            description: description.trim(),
            image: imageUrls[0],
            images: imageUrls,
            active,
            hasVariants,
            variants: hasVariants ? normalizedVariants : [],
            updated: new Date().toISOString(),
            ...(editId ? {} : { created: new Date().toISOString() }),
        }

        const ref = editId ? doc(db, SHOP_PRODUCTS_COLLECTION, editId) : doc(collection(db, SHOP_PRODUCTS_COLLECTION))
        await setDoc(ref, payload, { merge: true })
        toast.success(editId ? 'Product updated.' : 'Product created.')
        clearProductForm()
        setShowProductForm(false)
        fetchProducts()
    }

    const deleteProduct = async (id) => {
        await deleteDoc(doc(db, SHOP_PRODUCTS_COLLECTION, id))
        if (editId === id) {
            clearProductForm()
            setShowProductForm(false)
        }
        toast.success('Product deleted.')
        setDeleteProductId('')
        fetchProducts()
    }

    const saveSettings = async () => {
        const parsedCategories = settingsCategoriesCsv.split(',').map(row => row.trim()).filter(Boolean)
        const payload = {
            ...settings,
            categories: parsedCategories.length > 0 ? parsedCategories : DEFAULT_CATEGORIES,
            deliveryFee: toNumber(settings.deliveryFee),
            stripePublishableKey: deleteField(),
            stripeSecretKey: deleteField(),
            googlePlacesApiKey: deleteField(),
            updatedAt: new Date().toISOString(),
        }
        await setDoc(doc(db, SHOP_SETTINGS_COLLECTION, SHOP_SETTINGS_DOC), payload, { merge: true })
        setSettings(payload)
        setSettingsCategoriesCsv(payload.categories.join(','))
        toast.success('Shop settings saved.')
    }

    const setupOrderEdit = (order) => {
        setSelectedOrderId(order.id)
        setOrderStatus(order.status || 'Placed')
        setTrackingNumber(order.trackingNumber || '')
        setTrackingUrl(order.trackingUrl || '')
        setStatusNote('')
    }

    const updateOrderStatus = async () => {
        if (!selectedOrderId) {
            toast.error('Select an order first.')
            return
        }
        const token = await auth.currentUser.getIdToken()
        const response = await fetch(getFunctionUrl('updateShopOrderStatus'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token,
                orderId: selectedOrderId,
                status: orderStatus,
                trackingNumber,
                trackingUrl,
                note: statusNote,
            })
        })

        const data = await response.json()
        if (!response.ok || (data && data.err)) {
            toast.error((data && data.err) || 'Unable to update order')
            return
        }
        toast.success(data.emailQueued ? 'Order updated. Shipping email queued for the customer.' : 'Order updated.')
        fetchOrders()
    }

    return <div className="container">
        <Header />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
            <h2 style={{ margin: 0 }}>Shop Admin</h2>
            <a href="/shop-online" target="_blank" rel="noreferrer">Open public shop ↗</a>
            {loading && <span>Loading...</span>}
        </div>

        <Tabs tabs={['Products', 'Orders', 'Settings']} currentTab={activeTab} setCurrentTab={setActiveTab} />

        {activeTab === 'Products' && <>
            {!showProductForm && <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0 }}>Products</h3>
                    <button onClick={startAddProduct}>Add Product</button>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th>Stock</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map(row => <tr key={row.id}>
                            <td>{row.title}</td>
                            <td>{row.category}</td>
                            <td>${toNumber(row.price).toFixed(2)}</td>
                            <td>{toNumber(row.quantity)}</td>
                            <td>{row.active === false ? 'Inactive' : 'Active'}</td>
                            <td>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={() => setupEditProduct(row)}>Edit</button>
                                    <button onClick={() => setDeleteProductId(row.id)} style={{ backgroundColor: 'red' }}>Delete</button>
                                </div>
                            </td>
                        </tr>)}
                    </tbody>
                </table>
            </>}

            {showProductForm && <div style={{ border: '1px solid #d3d3d3', borderRadius: '8px', padding: '16px', marginBottom: '20px', maxWidth: '700px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>{editId ? 'Edit Product' : 'Add Product'}</h3>
                    <button onClick={() => { clearProductForm(); setShowProductForm(false) }} style={{ backgroundColor: '#fff', color: '#000' }}>Back to Products</button>
                </div>

                <Input label={'Title'} value={title} setValue={setTitle} errors={[]} name={'title'} />

                <div>
                    <label style={{ display: 'block', marginTop: '15px', fontSize: '14px' }}>Category</label>
                    <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: '100%', height: '42px', borderRadius: '6px', border: '1px solid #d3d3d3' }}>
                        {categories.map(row => <option key={row} value={row}>{row}</option>)}
                    </select>
                </div>

                <Input label={'Price (AUD)'} value={price} setValue={setPrice} errors={[]} name={'price'} type={'number'} />
                <div>
                    <label style={{ display: 'block', marginTop: '15px', fontSize: '14px' }}>Description</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={5}
                        style={{ width: '100%', borderRadius: '6px', border: '1px solid #d3d3d3', padding: '10px', fontFamily: 'inherit', fontSize: '14px' }}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={hasVariants} onChange={(e) => setHasVariants(e.target.checked)} />
                        Has variants
                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                        Active
                    </label>
                </div>

                {!hasVariants && <Input label={'Quantity in stock'} value={quantity} setValue={setQuantity} errors={[]} name={'quantity'} type={'number'} />}

                {hasVariants && <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ marginBottom: '8px' }}>Variants</h4>
                        <button onClick={addVariant}>Add Variant</button>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Value</th>
                                <th>Price Override</th>
                                <th>Quantity</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {variants.map(row => <tr key={row.id}>
                                <td>
                                    <input value={row.name} onChange={(e) => updateVariant(row.id, 'name', e.target.value)} placeholder="Size / Color" />
                                </td>
                                <td>
                                    <input value={row.value} onChange={(e) => updateVariant(row.id, 'value', e.target.value)} placeholder="S / Red" />
                                </td>
                                <td>
                                    <input type="number" value={row.price || ''} onChange={(e) => updateVariant(row.id, 'price', e.target.value)} placeholder="optional" />
                                </td>
                                <td>
                                    <input type="number" value={row.quantity} onChange={(e) => updateVariant(row.id, 'quantity', e.target.value)} />
                                </td>
                                <td>
                                    <button onClick={() => removeVariant(row.id)}>Remove</button>
                                </td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>}

                <div style={{ marginTop: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0 }}>Product Images</h4>
                        <button onClick={addProductImageSlot}>Add Image</button>
                    </div>
                    {productImages.map((row, index) => <div key={row.id} style={{ marginTop: '8px', border: '1px solid #eee', borderRadius: '6px', padding: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ fontSize: '13px' }}>Image {index + 1}</label>
                            {productImages.length > 1 && <button onClick={() => removeProductImage(row.id)} style={{ backgroundColor: '#fff', color: '#000' }}>Remove</button>}
                        </div>
                        <MyDropzone
                            storageLocation={`shop/products/${editId || 'new'}/image-${row.id}`}
                            image={row.url}
                            editing={true}
                            updateImage={(value) => updateProductImage(row.id, value)}
                            errors={[]}
                            name={`productImage-${row.id}`}
                            customMessage={'Drop product image or click to upload.'}
                        />
                    </div>)}
                </div>

                <div style={{ marginTop: '12px', display: 'flex', gap: '10px' }}>
                    <button onClick={saveProduct}>{editId ? 'Update Product' : 'Create Product'}</button>
                    <button onClick={() => { clearProductForm(); setShowProductForm(false) }} style={{ backgroundColor: '#fff', color: '#000' }}>Cancel</button>
                </div>
            </div>}
        </>}

        {activeTab === 'Settings' && <>
            <div style={{ border: '1px solid #d3d3d3', borderRadius: '8px', padding: '16px' }}>
                <h3>Shop Settings</h3>
                <Input label={'Categories (comma separated)'} value={settingsCategoriesCsv} setValue={setSettingsCategoriesCsv} errors={[]} name={'categories'} />
                <Input label={'Delivery fee (AUD)'} value={settings.deliveryFee?.toString()} setValue={(value) => setSettings({ ...settings, deliveryFee: value })} errors={[]} name={'deliveryFee'} type={'number'} />
                <MyDropzone
                    storageLocation={`shop/banner`}
                    image={settings.bannerImage}
                    editing={true}
                    updateImage={(value) => setSettings({ ...settings, bannerImage: value })}
                    errors={[]}
                    name={'shopBanner'}
                    customMessage={'Drop shop banner image or click to upload.'}
                />

                <button style={{ marginTop: '12px' }} onClick={saveSettings}>Save Settings</button>
            </div>
        </>}

        {activeTab === 'Orders' && <>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                <div>
                    <h3>Orders</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Total</th>
                                <th>Status</th>
                                <th>Tracking</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(row => <tr key={row.id} style={{ backgroundColor: selectedOrderId === row.id ? '#fff7ed' : 'white', cursor: 'pointer' }} onClick={() => setupOrderEdit(row)}>
                                <td>{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}</td>
                                <td>{row.userName || row.userEmail || '-'}</td>
                                <td>${toNumber(row.total).toFixed(2)}</td>
                                <td>{row.status || '-'}</td>
                                <td>{row.trackingNumber || '-'}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>

                <div style={{ border: '1px solid #d3d3d3', borderRadius: '8px', padding: '12px', height: 'fit-content' }}>
                    <h4>Update Order</h4>
                    {!selectedOrder ? <p>Select an order to edit</p> : <>
                        <p style={{ marginBottom: '8px' }}><b>Order:</b> {selectedOrder.id}</p>
                        <p style={{ marginBottom: '8px' }}><b>Customer:</b> {selectedOrder.userName || selectedOrder.userEmail || '-'}</p>
                        <p style={{ marginBottom: '8px' }}><b>Email:</b> {selectedOrder.userEmail || '-'}{selectedOrder.guest ? ' (Guest checkout)' : ''}</p>
                        <p style={{ marginBottom: '8px' }}><b>Address:</b> {selectedOrder.address?.formattedAddress || '-'}</p>

                        <div>
                            <label>Status</label>
                            <select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)} style={{ width: '100%', height: '42px', borderRadius: '6px', border: '1px solid #d3d3d3' }}>
                                {ORDER_STATUSES.map(row => <option key={row} value={row}>{row}</option>)}
                            </select>
                        </div>

                        <p>Mark a paid order as Shipped to email the customer. Updating tracking details on a shipped order sends an updated shipping email.</p>
                        <Input label={'Tracking number'} value={trackingNumber} setValue={setTrackingNumber} errors={[]} name={'trackingNumber'} />
                        <Input label={'Tracking URL'} value={trackingUrl} setValue={setTrackingUrl} errors={[]} name={'trackingUrl'} />
                        <Input label={'Note'} value={statusNote} setValue={setStatusNote} errors={[]} name={'statusNote'} />

                        <button onClick={updateOrderStatus}>Save Order Update</button>

                        <div style={{ marginTop: '12px' }}>
                            <h4>Items</h4>
                            {(selectedOrder.items || []).map(item => <div key={`${selectedOrder.id}-${item.productId}-${item.variantId || 'base'}`} style={{ borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '8px' }}>
                                <div>{item.productTitle} {item.variantLabel ? `(${item.variantLabel})` : ''}</div>
                                <div>Qty: {item.quantity}</div>
                                <div>Line: ${toNumber(item.lineTotal).toFixed(2)}</div>
                            </div>)}
                        </div>
                    </>}
                </div>
            </div>
        </>}

        <ModalComp showModal={!!deleteProductId} setShowModal={setDeleteProductId}>
            <h2>Delete Product?</h2>
            <p>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '10px' }}>
                <button style={{ backgroundColor: 'red' }} onClick={() => deleteProduct(deleteProductId)}>Delete</button>
                <button className="secondary" onClick={() => setDeleteProductId('')}>Cancel</button>
            </div>
        </ModalComp>
    </div>
}

export default Shop
